// ============================================================
// MySQLSecurityAndUserManagement.jsx
//
// Security & User Management (MySQL)
// Foundation · Module 10
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
} from "@mui/material";

/* Icons */
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import SecurityIcon from "@mui/icons-material/Security";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CodeIcon from "@mui/icons-material/Code";
import SchoolIcon from "@mui/icons-material/School";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#100720",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold: "#DAA520",
    goldBright: "#FFD700",
    goldGlow: "rgba(218,165,32,0.15)",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "rgba(0,0,0,0.45)",
    codeText: "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
};

/* ───────────────── SUB COMPONENTS ───────────────── */

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: BRAND.goldGlow,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading }}>
                {label}
            </Typography>
        </Box>
    );
}

function ConceptCard({ icon, title, description }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                height: "100%",
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 3,
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 18px ${BRAND.goldGlow}`,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold } })}
                <Typography sx={{ fontWeight: 700, color: BRAND.white }}>
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.7 }}>
                {description}
            </Typography>
        </Paper>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map(l => l.cmd).join("\n");

    const copy = () => {
        navigator.clipboard.writeText(fullText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, mb: 3 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map(c => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy"}>
                    <Box onClick={copy} sx={{ cursor: "pointer", color: BRAND.gold }}>
                        {copied ? <DoneIcon /> : <ContentCopyIcon />}
                    </Box>
                </Tooltip>
            </Box>

            <Box sx={{ p: 2 }}>
                {lines.map((l, i) => (
                    <Box key={i} sx={{ mb: 1 }}>
                        {l.comment && (
                            <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: BRAND.muted }}>
                                -- {l.comment}
                            </Typography>
                        )}
                        <Typography sx={{ fontFamily: "monospace", color: BRAND.codeText }}>
                            {l.cmd}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, mb: 2 }}>
            <Box
                onClick={() => setOpen(!open)}
                sx={{
                    p: 2,
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    background: BRAND.surface,
                }}
            >
                <Box sx={{ display: "flex", gap: 1 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold } })}
                    <Typography sx={{ color: BRAND.white }}>{title}</Typography>
                </Box>
                {open ? <ExpandLessIcon sx={{ color: BRAND.gold }} /> : <ExpandMoreIcon sx={{ color: BRAND.muted }} />}
            </Box>

            <Collapse in={open}>
                <Box sx={{ p: 2, background: "rgba(0,0,0,0.25)" }}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

/* ───────────────── MAIN PAGE ───────────────── */

export default function MySQLSecurityAndUserManagement() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: 6 }}>
            <Container maxWidth="lg">

                {/* HERO */}
                <Chip
                    icon={<SchoolIcon sx={{ color: BRAND.gold }} />}
                    label="Foundation · Module 10"
                    sx={{ mb: 2, background: BRAND.goldGlow, color: BRAND.gold }}
                />

                <Typography variant="h3" sx={{ ...sectionHeading, mb: 2 }}>
                    Security & <span style={{ color: BRAND.gold }}>User Management</span>
                </Typography>

                <Typography sx={{ color: BRAND.muted, maxWidth: 780 }}>
                    Databases store your most valuable assets. Proper user management,
                    permissions, and access control prevent breaches, data loss, and
                    catastrophic mistakes.
                </Typography>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* WHY */}
                <SectionTitle icon={<LightbulbIcon />} label="Why Database Security Matters" />
                <Typography sx={{ color: BRAND.muted, mb: 3 }}>
                    Many real‑world breaches come from over‑privileged database users.
                    Security starts with least privilege, strong authentication, and
                    clear separation of responsibilities.
                </Typography>

                {/* KEY CONCEPTS */}
                <SectionTitle icon={<MenuBookIcon />} label="Core Concepts" />
                <Grid container spacing={2}>
                    {[
                        {
                            icon: <PersonIcon />,
                            title: "Database Users",
                            description: "Each application or person should have a dedicated MySQL user.",
                        },
                        {
                            icon: <LockIcon />,
                            title: "Authentication",
                            description: "Passwords, host restrictions, and encrypted connections.",
                        },
                        {
                            icon: <AdminPanelSettingsIcon />,
                            title: "Privileges",
                            description: "GRANT and REVOKE control what users can do.",
                        },
                        {
                            icon: <SecurityIcon />,
                            title: "Least Privilege",
                            description: "Users get only the permissions they absolutely need.",
                        },
                        {
                            icon: <CodeIcon />,
                            title: "App vs Admin Roles",
                            description: "Separate application access from administrative access.",
                        },
                    ].map(c => (
                        <Grid item xs={12} sm={6} md={4} key={c.title}>
                            <ConceptCard {...c} />
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* PRACTICE */}
                <SectionTitle icon={<CodeIcon />} label="Hands‑On: User Management" />

                <CodeBlock
                    lines={[
                        {
                            comment: "Create a new application user",
                            cmd: "CREATE USER 'app_user'@'localhost' IDENTIFIED BY 'StrongPassword123!';",
                        },
                        {
                            comment: "Grant limited privileges",
                            cmd: "GRANT SELECT, INSERT, UPDATE ON academy.* TO 'app_user'@'localhost';",
                        },
                        {
                            comment: "Apply privilege changes",
                            cmd: "FLUSH PRIVILEGES;",
                        },
                    ]}
                />

                <ExpandableExample title="Revoking & Dropping Users" icon={<SecurityIcon />}>
                    <CodeBlock
                        lines={[
                            {
                                comment: "Remove dangerous permissions",
                                cmd: "REVOKE DELETE ON academy.* FROM 'app_user'@'localhost';",
                            },
                            {
                                comment: "Remove a user completely",
                                cmd: "DROP USER 'app_user'@'localhost';",
                            },
                        ]}
                    />
                </ExpandableExample>

                <ExpandableExample title="Read‑Only Reporting User" icon={<AdminPanelSettingsIcon />}>
                    <CodeBlock
                        lines={[
                            {
                                comment: "Create read-only user",
                                cmd: "CREATE USER 'report_user'@'%' IDENTIFIED BY 'ReportOnly!';",
                            },
                            {
                                comment: "Grant read-only access",
                                cmd: "GRANT SELECT ON academy.* TO 'report_user'@'%';",
                            },
                        ]}
                    />
                </ExpandableExample>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* LEARNED */}
                <SectionTitle icon={<StarIcon />} label="What You've Learned" />
                <Grid container spacing={2}>
                    {[
                        "How MySQL users and authentication work",
                        "How to create and remove database users",
                        "How GRANT and REVOKE control access",
                        "Why least privilege is critical",
                        "How to separate application and admin roles",
                        "Common security mistakes to avoid",
                    ].map(i => (
                        <Grid item xs={12} sm={6} key={i}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <CheckCircleOutlineIcon sx={{ color: BRAND.gold }} />
                                <Typography sx={{ color: BRAND.muted }}>{i}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />
                {/* FOOTER */}
                <Box sx={{ p: 3, background: BRAND.goldGlow, borderRadius: 2 }}>
                    <Typography sx={{ color: BRAND.white }}>
                        💡 <strong>Pro Tip:</strong> Never use the <code>root</code> account in applications.
                        Create separate users for apps, admins, and reporting. If credentials
                        leak, limited privileges can prevent catastrophic damage.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
